import { Button, Col, Modal, Row } from 'antd';
import { Fragment, useEffect, useState } from 'react';
import { v4 } from 'uuid';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { UserDataType } from '../../../type-definitions/api-types';
import {
  getMultipleInputElements,
  getSingleInputElement,
} from '../../../utils/get-input-element';
import LeafletMapMultiple from '../../../components-shared/LeafletMapMultiple';
import { FormElementsType } from '../helpers';

function DetailsTab({
  formElements,
  inputChangedHandler,
  handleCancelRedirect,
  isEditable,
  userData,
  onBoundsMarkerMove,
}: {
  formElements: FormElementsType;
  inputChangedHandler: (name: any, value: any) => void;
  handleCancelRedirect: () => void;
  isEditable: boolean;
  userData: Partial<UserDataType>;
  onBoundsMarkerMove: (
    lat: number,
    lng: number,
    name: 'topLeft' | 'bottomRight'
  ) => void;
}) {
  const [mapVisibility, setMapVisibility] = useState<boolean>(false);
  const [mapZoom, setMapZoom] = useState<number>(10);

  const [mapList, setMapList] = useState<Array<any>>([
    {
      uuid: v4(),
      lat: userData.lat || 0,
      lng: (userData.lng || 0) - 0.05,
      markerName: 'topLeft',
      iconColor: 'blue',
    },
    {
      uuid: v4(),
      lat: userData.lat || 0,
      lng: (userData.lng || 0) + 0.05,
      markerName: 'bottomRight',
      iconColor: 'red',
    },
  ]);

  useEffect(() => {
    if (
      formElements.topLeftLat.value &&
      formElements.topLeftLng.value &&
      formElements.bottomRightLat.value &&
      formElements.bottomRightLng.value
    ) {
      let tempMapList = [...mapList];
      tempMapList = tempMapList.map((item) => {
        if (item.markerName === 'topLeft') {
          item.lat = parseFloat(formElements.topLeftLat.value);
          item.lng = parseFloat(formElements.topLeftLng.value);
        }

        if (item.markerName === 'bottomRight') {
          item.lat = parseFloat(formElements.bottomRightLat.value);
          item.lng = parseFloat(formElements.bottomRightLng.value);
        }

        return item;
      });

      setMapList(tempMapList);
    } else if (formElements.topLeftLat.value && formElements.topLeftLng.value) {
      let tempMapList = [...mapList];
      tempMapList = tempMapList.map((item) => {
        if (item.markerName === 'topLeft') {
          item.lat = parseFloat(formElements.topLeftLat.value);
          item.lng = parseFloat(formElements.topLeftLng.value);
        }
        return item;
      });

      setMapList(tempMapList);
    } else if (
      formElements.bottomRightLat.value &&
      formElements.bottomRightLng.value
    ) {
      let tempMapList = [...mapList];
      tempMapList = tempMapList.map((item) => {
        if (item.markerName === 'bottomRight') {
          item.lat = parseFloat(formElements.bottomRightLat.value);
          item.lng = parseFloat(formElements.bottomRightLng.value);
        }

        return item;
      });

      setMapList(tempMapList);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formElements.topLeftLat.value,
    formElements.topLeftLng.value,
    formElements.bottomRightLat.value,
    formElements.bottomRightLng.value,
  ]);

  const tempFormElements = { ...formElements };
  const single = getSingleInputElement({
    formElements: tempFormElements,
    inputChangedHandler,
    sliceValue: [0, 3],
  });

  // North West
  const multiple = getMultipleInputElements({
    formElements: tempFormElements,
    inputChangedHandler,
    sliceValue: [3, 5],
  });

  // South East
  const multiple2 = getMultipleInputElements({
    formElements: tempFormElements,
    inputChangedHandler,
    sliceValue: [5, 7],
  });

  function handleMarkerMove(
    lat: number,
    lng: number,
    markerName: 'topLeft' | 'bottomRight'
  ) {
    onBoundsMarkerMove(lat, lng, markerName);
  }

  return (
    <Fragment>
      {single}
      <Row align="middle">
        <Col lg={20}>
          {multiple}
          {multiple2}
        </Col>
        <Col lg={4}>
          <Row>
            <Col className="text-center" lg={24}>
              <Button
                size="small"
                type="primary"
                htmlType="button"
                onClick={() => setMapVisibility(true)}>
                MAP
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>

      <Row justify="center" className="pt-3">
        {isEditable && (
          <Col xs={24} sm={12} md={8}>
            <Button block type="primary" htmlType="submit" size="large">
              Submit
            </Button>
          </Col>
        )}

        <Col
          xs={24}
          sm={12}
          md={8}
          className="pl-lg-3 pl-md-2 pl-sm-3 pt-2 pt-sm-0">
          <Button
            block
            type="primary"
            htmlType="button"
            size="large"
            onClick={handleCancelRedirect}>
            Cancel
          </Button>
        </Col>
      </Row>

      {mapVisibility && (
        <Fragment>
          <Modal
            width={'60%'}
            closable={false}
            footer={null}
            visible={mapVisibility}
            onCancel={() => setMapVisibility(false)}>
            <Fragment>
              <div className="row">
                <div
                  className="col-6 text-center"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                  <FaMapMarkerAlt
                    size="3em"
                    style={{
                      color: '#2596be',
                    }}
                  />{' '}
                  Top Left
                </div>
                <div
                  className="col-6 text-center"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                  <FaMapMarkerAlt
                    size="3em"
                    style={{
                      color: '#ed0e1d',
                    }}
                  />{' '}
                  Bottom Right
                </div>
              </div>
              <div className="row py-2">
                <div className="col-12">
                  <LeafletMapMultiple
                    center={[userData.lat || 0, userData.lng || 0]}
                    mapList={mapList}
                    zoom={mapZoom}
                    onMapZoom={(zoom) => setMapZoom(zoom)}
                    onMarkerMove={handleMarkerMove}
                    styles={{ height: '50vh' }}
                    markerProps={{ draggable: isEditable }}
                  />
                </div>
              </div>
            </Fragment>
          </Modal>
        </Fragment>
      )}
    </Fragment>
  );
}

export default DetailsTab;
